import api, { handleResponse, handleError } from './api';

const eventService = {
  /**
   * Get events with optional status filter
   * @param {string} [status] - Filter by raw backend status: 'draft', 'selling',
   *   'funding', 'processing_greenlight', 'greenlit', 'ended', 'dissolved', 'canceled'
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEvents(status) {
    try {
      const params = status ? { status } : {};
      const response = await api.get('/events', { params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** The top bar's HOST bubble: `{ liveEventCount }` — the caller's running events. */
  async getHostSummary() {
    try {
      return handleResponse(await api.get('/events/host-summary'));
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get a single event by ID
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEvent(id) {
    try {
      const response = await api.get(`/events/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get an event by its invite code
   * @param {string} inviteCode - Event invite code
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEventByCode(inviteCode) {
    try {
      const response = await api.get(`/events/code/${inviteCode}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get an event by its canonical URL parts (/e/{citySlug}/{slug}).
   * @param {string} citySlug - e.g. "kelowna"
   * @param {string} slug - e.g. "intro-to-yoga-series"
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getEventBySlug(citySlug, slug) {
    try {
      const response = await api.get(
        `/events/by-slug/${encodeURIComponent(citySlug)}/${encodeURIComponent(slug)}`
      );
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create a new event
   * @param {object} data - Event data
   * @param {string} data.title - Event title
   * @param {string} data.description - Event description
   * @param {string} data.date - Event date (ISO string)
   * @param {string} data.location - Event location
   * @param {number} data.capacity - Maximum attendees
   * @param {number} [data.coverCharge] - Cover charge amount
   * @param {string} [data.dressCode] - Dress code
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async createEvent(data) {
    try {
      const response = await api.post('/events', data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Whether the caller may use the free RSVP-goal option (has run a Party Starter). Phase 10. */
  async getRsvpGoalEligibility() {
    try {
      return handleResponse(await api.get('/events/rsvp-goal-eligibility'));
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Update an existing event
   * @param {string} id - Event ID
   * @param {object} data - Event data to update
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async updateEvent(id, data) {
    try {
      const response = await api.put(`/events/${id}`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Delete an event
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async deleteEvent(id) {
    try {
      const response = await api.delete(`/events/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Publish an event (change status from draft to published)
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async publishEvent(id) {
    try {
      const response = await api.post(`/events/${id}/publish`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Greenlight an event (confirm it will happen)
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async greenlightEvent(id) {
    try {
      const response = await api.post(`/events/${id}/greenlight`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Cancel an event
   * @param {string} id - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async cancelEvent(id) {
    try {
      const response = await api.post(`/events/${id}/cancel`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Upload a poster image for an event
   * @param {string} id - Event ID
   * @param {File} file - The image file to upload
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  /**
   * Save the review step's cover settings (layout, photo count, order, focal points, grid card).
   * Every field is optional. Answers with the refreshed EventDto.
   * @param {string} id
   * @param {{layout?:string, split?:number, photoCount?:number, order?:string[], focal?:{id:string,x:number,y:number}[], cardId?:string, cardFocalX?:number, cardFocalY?:number}} data
   */
  async updateCover(id, data) {
    try {
      return handleResponse(await api.put(`/events/${id}/cover`, data));
    } catch (error) {
      return handleError(error);
    }
  },

  async uploadPoster(id, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/events/${id}/poster`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
  /**
   * Get invitable friends for an event
   * @param {string} eventId - Event ID
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getInvitableFriends(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/invitable-friends`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Invite a friend to an event
   * @param {string} eventId - Event ID
   * @param {string} userId - User ID of friend to invite
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async inviteFriend(eventId, userId) {
    try {
      const response = await api.post(`/events/${eventId}/invite`, { userId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async agreeToHouseRules(eventId) {
    try {
      const response = await api.post(`/events/${eventId}/house-rules/agree`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async claimPartySupply(supplyId, claim) {
    try {
      const response = await api.post(`/events/party-supplies/${supplyId}/claim`, { claim });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async uploadPartySupplyImage(supplyId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/events/party-supplies/${supplyId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async claimVolunteerTask(taskId, claim) {
    try {
      const response = await api.post(`/events/volunteer-tasks/${taskId}/claim`, { claim });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async uploadVolunteerTaskImage(taskId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/events/volunteer-tasks/${taskId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async proposePartyTrick(eventId, text) {
    try {
      const response = await api.post(`/events/${eventId}/party-tricks`, { text });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async reviewPartyTrick(trickId, status) {
    try {
      const response = await api.post(`/events/party-tricks/${trickId}/review`, { status });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ── Party Questions (host Q&A with roll-up) ──────────────────────────────
  async getEventQuestions(eventId) {
    try { return handleResponse(await api.get(`/events/${eventId}/questions`)); }
    catch (error) { return handleError(error); }
  },

  async createEventQuestion(eventId, { prompt, type, options }) {
    try { return handleResponse(await api.post(`/events/${eventId}/questions`, { prompt, type, options })); }
    catch (error) { return handleError(error); }
  },

  async deleteEventQuestion(questionId) {
    try { return handleResponse(await api.delete(`/events/questions/${questionId}`)); }
    catch (error) { return handleError(error); }
  },

  async respondToEventQuestion(questionId, { selectedOption = null, textAnswer = null }) {
    try { return handleResponse(await api.post(`/events/questions/${questionId}/respond`, { selectedOption, textAnswer })); }
    catch (error) { return handleError(error); }
  },

  async deletePartyTrick(trickId) {
    try {
      const response = await api.delete(`/events/party-tricks/${trickId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async toggleBookmark(eventId) {
    try {
      const response = await api.post(`/events/${eventId}/bookmark`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getMyBookmarks() {
    try {
      const response = await api.get('/events/bookmarks');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Register interest after a guest clicks the external "Buy Tickets" link on an
   * external-ticketing event. Adds them to the host's interested roster.
   */
  async registerExternalInterest(eventId) {
    try {
      const response = await api.post(`/events/${eventId}/external-interest`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Fetch the guest list (only visible to confirmed guests + host). */
  async getGuestList(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/guest-list`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Toggle whether the current guest is shown by name on the guest list. */
  async setGuestListVisibility(eventId, show) {
    try {
      const response = await api.put(`/events/${eventId}/guest-list-visibility`, { show });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async duplicateEvent(id) {
    try {
      const response = await api.post(`/events/${id}/duplicate`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getMyInvitedEvents() {
    try {
      const response = await api.get('/events/invited');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * PASS on an invitation (guest dashboard). The invite leaves the list for good;
   * the person who sent it is never told.
   */
  async passInvitation(eventId) {
    try {
      const response = await api.post(`/events/${eventId}/invitations/pass`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async verifyEvent(eventId, data) {
    try {
      const response = await api.post(`/events/${eventId}/verify`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Host money summary for one event: ticket revenue, refunds, escrow held / released,
   * fees per funding type, net to host, and a per-transaction ledger.
   * GET /events/{id}/money (host/co-host). 404 while the endpoint is landing — callers
   * fall back to computing sales/refunds from the applications list.
   */
  async getEventMoney(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/money`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getVerifications(eventId) {
    try {
      const response = await api.get(`/events/${eventId}/verifications`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Live public events near a city, optionally narrowed to one category.
   * Backs the crawlable /events/in/{citySlug}[/{category}] landing pages.
   * Anonymous — no auth required.
   *
   * @param {string} citySlug - kebab city key (see backend PortalCities)
   * @param {string|null} [category] - canonical category slug, or null for all
   * @param {{page?: number, pageSize?: number}} [opts]
   */
  async getEventsByCity(citySlug, category = null, { page = 1, pageSize = 24 } = {}) {
    try {
      const path = category
        ? `/events/city/${encodeURIComponent(citySlug)}/${encodeURIComponent(category)}`
        : `/events/city/${encodeURIComponent(citySlug)}`;
      const response = await api.get(path, { params: { page, pageSize } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ── PUT IT ON A PAGE (event builder → Pro pages) ─────────────────────────
  // docs/plans/2026-09-pro-multipage-events/PLAN.md "Event builder". The caller
  // must manage the page; the server links any public event or one the caller hosts.

  /** Lists this event on a Pro page. POST /pro/pages/{pageId}/events { eventId } → PageEventDto. Idempotent. */
  async linkPageEvent(pageId, eventId) {
    try {
      const response = await api.post(`/pro/pages/${pageId}/events`, { eventId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Takes this event off a Pro page. DELETE /pro/pages/{pageId}/events/{eventId} */
  async unlinkPageEvent(pageId, eventId) {
    try {
      const response = await api.delete(`/pro/pages/${pageId}/events/${eventId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};

export default eventService;
